import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  MessageSquare,
  Plus,
  LogOut,
  Menu,
  X,
  Tractor,
  UtensilsCrossed
} from 'lucide-react';
import { getCurrentMarketplaceUser, logoutMarketplaceUser, isFarmer } from '../utils/users';
import logo from '../assets/logo-nuturaa.svg';

const MarketplaceHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const user = getCurrentMarketplaceUser();
  const userIsFarmer = isFarmer();

  const handleLogout = () => {
    logoutMarketplaceUser();
    navigate('/');
  };

  const farmerNavItems = [
    { path: '/farmer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/farmer/listings/new', label: 'New Listing', icon: Plus },
    { path: '/messages', label: 'Messages', icon: MessageSquare },
  ];

  const restaurantNavItems = [
    { path: '/marketplace', label: 'Browse', icon: Store },
    { path: '/messages', label: 'Messages', icon: MessageSquare },
  ];

  const navItems = userIsFarmer ? farmerNavItems : restaurantNavItems;

  const isActive = (path) => {
    if (path === '/marketplace') {
      return location.pathname === '/marketplace' || location.pathname.startsWith('/marketplace/');
    }
    if (path === '/farmer/dashboard') {
      return location.pathname === '/farmer/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Role Badge */}
          <div className="flex items-center gap-4">
            <Link to={userIsFarmer ? '/farmer/dashboard' : '/marketplace'} className="flex items-center gap-2">
              <img src={logo} alt="Nuturaa" className="h-8 w-8" />
              <span className="text-xl font-semibold text-stone-900 hidden sm:inline">Nuturaa</span>
            </Link>
            <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              userIsFarmer
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {userIsFarmer ? (
                <>
                  <Tractor className="w-3 h-3" />
                  Farmer
                </>
              ) : (
                <>
                  <UtensilsCrossed className="w-3 h-3" />
                  Restaurant
                </>
              )}
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {/* Desktop User Info */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-stone-900">
                  {user?.business_name || user?.name}
                </div>
                <div className="text-xs text-stone-500">{user?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-stone-600 hover:bg-stone-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stone-200 bg-white">
          <div className="px-4 py-3 border-b border-stone-100">
            <div className="text-sm font-medium text-stone-900">
              {user?.business_name || user?.name}
            </div>
            <div className="text-xs text-stone-500">{user?.email}</div>
          </div>
          <nav className="px-2 py-2 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-stone-600 hover:text-stone-900 hover:bg-stone-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Sign out
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default MarketplaceHeader;
